import React, { useEffect, useRef, useState } from 'react';
import * as PIXI from 'pixi.js';
import { Grid } from '../game/Grid';
import { ToolRegistry } from '../game/tools/ToolRegistry';
import { MATERIALS, ACTIONS, TOOLS, PARTICLE_NAMES } from '../game/constants';
import type { ParticleType } from '../game/constants';
import { updateBuffer, getParticleColor } from '../utils/updateBuffer';
import { useGravity } from '../hooks/useGravity';
import { HintsBar } from './ui/HintsBar';
import { MaterialPanel } from './ui/MaterialPanel';
import { ToolsPanel } from './ui/ToolsPanel';
import { MainToolbar } from './ui/MainToolbar';
import { InfoPanel } from './ui/InfoPanel';
import { GravityCompass } from './ui/GravityCompass';
import { useLocale } from '../i18n';

export const GameCanvas = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const appRef = useRef<PIXI.Application | null>(null);
    const locale = useLocale();
    const [selectedParticle, setSelectedParticle] = useState<ParticleType>('sand');
    const [hoverInfo, setHoverInfo] = useState<{ name: string; temp: number } | null>(null);
    const currentHoverInfoRef = useRef<{ name: string; temp: number } | null>(null);
    const emptyFramesRef = useRef(0);
    const selectedParticleRef = useRef<ParticleType>('sand');
    const brushSizeRef = useRef<number>(3); // 默认半径3
    const [brushSize, setBrushSize] = useState(3); // 用于 UI 显示
    const glowSpriteRef = useRef<PIXI.Sprite | null>(null);
    const [enableGlow, setEnableGlow] = useState(true);
    const [isMaterialPanelOpen, setIsMaterialPanelOpen] = useState(false);
    const [isToolsPanelOpen, setIsToolsPanelOpen] = useState(false);
    const [isBrushPanelOpen, setIsBrushPanelOpen] = useState(false);
    const [currentMaterial, setCurrentMaterial] = useState<ParticleType>('sand');
    const [currentTool, setCurrentTool] = useState<ParticleType>('eraser');
    
    // Grid 引用，用于访问 grid 实例的方法（如 clear）
    const gridRef = useRef<Grid | null>(null);
    // 用于处理点击外部关闭面板
    const materialPanelRef = useRef<HTMLDivElement>(null);
    const toolsPanelRef = useRef<HTMLDivElement>(null);
    const brushPanelRef = useRef<HTMLDivElement>(null);
    const toolbarRef = useRef<HTMLDivElement>(null);
    const hintsRef = useRef<HTMLDivElement>(null);

    // 调试信息状态
    const [debugInfo, setDebugInfo] = useState<string>('');
    
    // 使用自定义 Hook 处理重力
    const { 
        gravityEnabled, 
        setGravityEnabled, 
        gravityVector, 
        simulateGravity, 
        setSimulateGravity, 
        toggleGravity 
    } = useGravity(gridRef, setDebugInfo);
    
    // 是否为本地开发环境
    const isLocalDev = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    // 绘制形状模式
    const shapeModeRef = useRef<'line' | 'rect' | null>(null);
    
    useEffect(() => {
        selectedParticleRef.current = selectedParticle;
        
        // 如果选中的是材质，更新 currentMaterial
        const isMaterial = MATERIALS.some(m => m.type === selectedParticle);
        if (isMaterial) {
            setCurrentMaterial(selectedParticle);
        }
        
        // 如果选中的是工具，更新 currentTool
        const isAction = ACTIONS.some(a => a.type === selectedParticle);
        if (isAction) {
            setCurrentTool(selectedParticle);
        }
    }, [selectedParticle]);

    useEffect(() => {
        if (glowSpriteRef.current) {
            glowSpriteRef.current.visible = enableGlow;
        }
    }, [enableGlow]);

    // 快捷键监听
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const key = e.key.toLowerCase();
            
            // 清空画布快捷键 X
            if (key === 'x') {
                gridRef.current?.clear();
                return;
            }

            const tool = TOOLS.find(t => t.shortcut === key);
            if (tool) {
                setSelectedParticle(tool.type);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);
    
    const closePanels = () => {
        setIsMaterialPanelOpen(false);
        setIsToolsPanelOpen(false);
        setIsBrushPanelOpen(false);
    };

    // 点击外部关闭面板
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            const target = event.target as Node;
            const isToolbarClick = toolbarRef.current && toolbarRef.current.contains(target);
            const isHintsClick = hintsRef.current && hintsRef.current.contains(target);

            if (isMaterialPanelOpen && 
                materialPanelRef.current && 
                !materialPanelRef.current.contains(target) &&
                !isToolbarClick) {
                setIsMaterialPanelOpen(false);
            }
            
            if (isToolsPanelOpen && 
                toolsPanelRef.current && 
                !toolsPanelRef.current.contains(target) &&
                !isToolbarClick) {
                setIsToolsPanelOpen(false);
            }

            if (isBrushPanelOpen && 
                brushPanelRef.current && 
                !brushPanelRef.current.contains(target) &&
                !isHintsClick) {
                setIsBrushPanelOpen(false);
            }
        };

        window.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('touchstart', handleClickOutside);
        
        return () => {
            window.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isMaterialPanelOpen, isToolsPanelOpen, isBrushPanelOpen]);

    useEffect(() => {
        if (!containerRef.current) return;

        const init = async () => {
            // 避免重复初始化
            if (appRef.current) return;

            const app = new PIXI.Application();
            // 改为手动控制 resize，不依赖 resizeTo: window
            // resizeTo: containerRef.current 也可以，但为了配合 ResizeObserver，我们手动 resize
            await app.init({ 
                resizeTo: containerRef.current!,
                backgroundColor: 0x000000,
            });
            
            if (!containerRef.current) {
                app.destroy();
                return;
            }
            containerRef.current.appendChild(app.canvas);
            appRef.current = app;

            // 设置缩放比例，让像素更大
            const scale = 4;
            let width = Math.ceil(app.screen.width / scale);
            let height = Math.ceil(app.screen.height / scale);
            
            const grid = new Grid(width, height);
            gridRef.current = grid;
            let buffer = new Uint8Array(width * height * 4);
            
            // 初始填充
            updateBuffer(grid, buffer);

            // Pixi v8 中使用 Texture 和 BufferImageSource
            let source = new PIXI.BufferImageSource({
                resource: buffer,
                width,
                height,
                format: 'rgba8unorm',
                scaleMode: 'nearest',
            });
            let texture = new PIXI.Texture({ source });

            const sprite = new PIXI.Sprite(texture);
            sprite.width = app.screen.width;
            sprite.height = app.screen.height;
            
            // 创建发光层 (Bloom effect)
            const glowSprite = new PIXI.Sprite(texture);
            glowSprite.width = app.screen.width;
            glowSprite.height = app.screen.height;
            glowSprite.blendMode = 'add'; // 叠加模式
            glowSprite.alpha = 0.5; // 发光强度
            glowSprite.visible = enableGlow; // 默认显示，与 state 保持一致
            glowSpriteRef.current = glowSprite;
            
            // 添加模糊滤镜
            const blurFilter = new PIXI.BlurFilter();
            blurFilter.strength = 6; // 模糊半径
            blurFilter.quality = 2; // 模糊质量
            glowSprite.filters = [blurFilter];

            app.stage.addChild(sprite);
            app.stage.addChild(glowSprite);

            // 笔刷轮廓
            const cursor = new PIXI.Graphics();
            app.stage.addChild(cursor);

            const updateCursor = (x: number, y: number) => {
                cursor.clear();
                // 如果在画布外，不显示
                if (x < 0 || y < 0 || x > app.screen.width || y > app.screen.height) return;

                const r = brushSizeRef.current;
                const size = (r * 2 + 1) * scale;
                
                // 绘制白色边框，稍微有点透明度
                cursor.rect(
                    x - (r + 0.5) * scale, 
                    y - (r + 0.5) * scale, 
                    size, 
                    size
                );
                cursor.stroke({ width: 1, color: 0xffffff, alpha: 0.5 });

                // 拖拽预览：绘制被拖拽的粒子
                if (isDrawing) {
                    const toolName = selectedParticleRef.current;
                    if (toolName === 'drag') {
                        const tool = ToolRegistry.getInstance().getTool('drag') as any;
                        if (tool && tool.draggingParticles) {
                            const particles = tool.draggingParticles;
                            for (const p of particles) {
                                // 相对坐标转为屏幕坐标
                                // p.dx, p.dy 是相对于鼠标中心的偏移
                                const px = x + p.dx * scale;
                                const py = y + p.dy * scale;
                                
                                // 绘制粒子
                                cursor.rect(px, py, scale, scale);
                                cursor.fill({ color: getParticleColor(p.particle) });
                            }
                        }
                    }
                }

                // 直线预览
                if (isDrawing && lineStartPos) {
                    const startX = lineStartPos.x * scale;
                    const startY = lineStartPos.y * scale;
                    
                    if (shapeModeRef.current === 'rect') {
                        // 矩形预览
                        const minX = Math.min(startX, x);
                        const minY = Math.min(startY, y);
                        const w = Math.abs(x - startX);
                        const h = Math.abs(y - startY);
                        cursor.rect(minX, minY, w, h);
                        cursor.stroke({ width: 1, color: 0xffffff, alpha: 0.8 });
                    } else {
                        // 直线预览
                        cursor.moveTo(startX, startY);
                        cursor.lineTo(x, y);
                        cursor.stroke({ width: 1, color: 0xffffff, alpha: 0.8 });
                    }
                }
            };

            // 滚轮调整笔刷大小
            const handleWheel = (e: WheelEvent) => {
                // 阻止默认滚动行为（如页面滚动）
                e.preventDefault();
                
                let newSize = brushSizeRef.current;
                if (e.deltaY < 0) {
                    newSize = Math.min(newSize + 1, 20); // 最大半径20
                } else {
                    newSize = Math.max(newSize - 1, 0); // 最小半径0 (1x1像素)
                }

                brushSizeRef.current = newSize;
                setBrushSize(newSize);

                // 立即更新光标
                // 注意：这里需要获取当前的鼠标位置，由于 wheel 事件不包含 global 位置，
                // 我们依赖 pointermove 更新的 lastGlobalPos 或者直接从事件获取 clientX/Y
                const rect = app.canvas.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const y = e.clientY - rect.top;
                updateCursor(x, y);
            };
            
            app.canvas.addEventListener('wheel', handleWheel, { passive: false });

            // 交互事件
            app.stage.eventMode = 'static';
            app.stage.hitArea = app.screen;
            
            let isDrawing = false;
            let lastGridPos = { x: -1, y: -1 };
            // 用于记录原始鼠标位置，用于 updateCursor
            let lastGlobalPos = { x: -1, y: -1 };
            // 直线绘制模式起点
            let lineStartPos: { x: number, y: number } | null = null;
            
            // 记录上一次绘制的位置和中间点，用于平滑插值
            const prevDrawPosRef = { current: { x: 0, y: 0 } };
            const prevMidPosRef = { current: { x: 0, y: 0 } };

            const drawBrush = (x: number, y: number) => {
                // 使用动态笔刷大小
                const brushRadius = brushSizeRef.current;
                const toolName = selectedParticleRef.current;
                const tool = ToolRegistry.getInstance().getTool(toolName);
                
                if (tool) {
                    tool.apply(x, y, grid, brushRadius);
                }
            };

            // 线性插值（Bresenham算法）- 用于最后一段封口
            const drawLine = (p0: {x: number, y: number}, p1: {x: number, y: number}) => {
                let x0 = Math.floor(p0.x);
                let y0 = Math.floor(p0.y);
                const x1 = Math.floor(p1.x);
                const y1 = Math.floor(p1.y);
                
                const dx = Math.abs(x1 - x0);
                const dy = Math.abs(y1 - y0);
                const sx = (x0 < x1) ? 1 : -1;
                const sy = (y0 < y1) ? 1 : -1;
                let err = dx - dy;

                while(true) {
                    drawBrush(x0, y0);
                    if ((x0 === x1) && (y0 === y1)) break;
                    const e2 = 2 * err;
                    if (e2 > -dy) { err -= dy; x0 += sx; }
                    if (e2 < dx) { err += dx; y0 += sy; }
                }
            };

            // 二次贝塞尔曲线插值 - 用于平滑笔触
            // p0: 起点, p1: 控制点, p2: 终点
            const drawQuadCurve = (p0: {x: number, y: number}, p1: {x: number, y: number}, p2: {x: number, y: number}) => {
                // 估算步数：距离除以步长（0.5像素）
                const d1 = Math.abs(p1.x - p0.x) + Math.abs(p1.y - p0.y);
                const d2 = Math.abs(p2.x - p1.x) + Math.abs(p2.y - p1.y);
                const steps = Math.floor((d1 + d2) * 2); // 乘以2增加密度
                
                if (steps === 0) return;

                for (let i = 0; i <= steps; i++) {
                    const t = i / steps;
                    const x = Math.pow(1 - t, 2) * p0.x + 2 * (1 - t) * t * p1.x + Math.pow(t, 2) * p2.x;
                    const y = Math.pow(1 - t, 2) * p0.y + 2 * (1 - t) * t * p1.y + Math.pow(t, 2) * p2.y;
                    drawBrush(Math.floor(x), Math.floor(y));
                }
            };

            app.stage.on('pointerdown', (e) => {
                // 如果点击的是 UI 元素，不处理
                // 但这里 pointerdown 是绑定在 stage 上的，通常只有点击 canvas 才会触发
                // 如果有 React UI 覆盖在上面，React 的事件会先触发
                
                const originalEvent = e.originalEvent as any;

                // 处理 Shift/Ctrl 键
                if (originalEvent.shiftKey && originalEvent.metaKey) {
                    // 矩形模式
                    shapeModeRef.current = 'rect';
                } else if (originalEvent.shiftKey || originalEvent.metaKey) {
                    // 直线模式
                    shapeModeRef.current = 'line';
                } else {
                    shapeModeRef.current = null;
                }

                isDrawing = true;
                const pos = e.getLocalPosition(app.stage);
                const gx = Math.floor(pos.x / scale);
                const gy = Math.floor(pos.y / scale);
                
                // 处理工具的 handleDown
                const toolName = selectedParticleRef.current;
                const tool = ToolRegistry.getInstance().getTool(toolName);
                if (tool && tool.handleDown) {
                    tool.handleDown(gx, gy, grid, brushSizeRef.current);
                }

                if (shapeModeRef.current) {
                    // 形状模式：记录起点，不立即绘制
                    lineStartPos = { x: gx, y: gy };
                } else {
                    // 普通笔刷模式
                    drawBrush(gx, gy);
                    
                    // 初始化平滑插值状态
                    prevDrawPosRef.current = { x: gx, y: gy };
                    prevMidPosRef.current = { x: gx, y: gy };
                }
                
                lastGridPos = { x: gx, y: gy };
            });

            // 存储当前鼠标所在的网格位置，用于实时查询温度
            const mouseGridPosRef = { current: { x: -1, y: -1 } };

            app.stage.on('pointermove', (e) => {
                const pos = e.getLocalPosition(app.stage);
                lastGlobalPos = { x: pos.x, y: pos.y };
                updateCursor(pos.x, pos.y);

                const gx = Math.floor(pos.x / scale);
                const gy = Math.floor(pos.y / scale);

                // 更新鼠标网格位置
                if (gx >= 0 && gx < width && gy >= 0 && gy < height) {
                    mouseGridPosRef.current = { x: gx, y: gy };
                } else {
                    mouseGridPosRef.current = { x: -1, y: -1 };
                }

                if (!isDrawing) return;

                // 如果处于形状模式，只更新预览（updateCursor 已经处理），不绘制
                if (shapeModeRef.current && lineStartPos) {
                    return;
                }

                // 普通笔刷模式：插值绘制
                // 简单防抖：如果格子坐标没变，不处理
                if (gx === lastGridPos.x && gy === lastGridPos.y) return;

                // 计算当前点和上一点的中点
                const midPoint = {
                    x: (prevDrawPosRef.current.x + gx) / 2,
                    y: (prevDrawPosRef.current.y + gy) / 2
                };

                // 使用二次贝塞尔曲线连接：上一个中点 -> 上一个点（作为控制点） -> 当前中点
                drawQuadCurve(prevMidPosRef.current, prevDrawPosRef.current, midPoint);

                // 更新状态
                prevMidPosRef.current = midPoint;
                prevDrawPosRef.current = { x: gx, y: gy };
                lastGridPos = { x: gx, y: gy };
            });

            const endDrawing = (e: PIXI.FederatedPointerEvent) => {
                if (!isDrawing) return;
                
                // 处理工具的 handleUp
                const pos = e.getLocalPosition(app.stage);
                const gx = Math.floor(pos.x / scale);
                const gy = Math.floor(pos.y / scale);
                
                const toolName = selectedParticleRef.current;
                const tool = ToolRegistry.getInstance().getTool(toolName);
                if (tool && tool.handleUp) {
                    tool.handleUp(gx, gy, grid, brushSizeRef.current);
                }

                // 处理形状绘制结束
                if (shapeModeRef.current && lineStartPos) {
                    if (shapeModeRef.current === 'rect') {
                        // 绘制矩形
                        // 简单实现：四条直线
                        const startX = lineStartPos.x;
                        const startY = lineStartPos.y;
                        const endX = gx;
                        const endY = gy;
                        
                        drawLine({x: startX, y: startY}, {x: endX, y: startY});
                        drawLine({x: endX, y: startY}, {x: endX, y: endY});
                        drawLine({x: endX, y: endY}, {x: startX, y: endY});
                        drawLine({x: startX, y: endY}, {x: startX, y: startY});
                    } else {
                        // 绘制直线
                        drawLine(lineStartPos, { x: gx, y: gy });
                    }
                } else if (isDrawing) {
                    // 普通笔刷结束：封口，从上一个中点画直线到当前点
                    drawLine(prevMidPosRef.current, prevDrawPosRef.current);
                }

                isDrawing = false;
                lineStartPos = null;
                shapeModeRef.current = null;
                // 强制更新光标以去除预览线
                if (lastGlobalPos.x >= 0) {
                    updateCursor(lastGlobalPos.x, lastGlobalPos.y);
                }
            };

            app.stage.on('pointerup', endDrawing);
            app.stage.on('pointerupoutside', endDrawing);

            // 游戏循环
            app.ticker.add(() => {
                // 长按持续渲染：即使鼠标不动，也持续在当前位置产生粒子
                if (isDrawing && !shapeModeRef.current && lastGridPos.x !== -1) {
                    drawBrush(lastGridPos.x, lastGridPos.y);
                }

                grid.update();

                // 实时更新悬停信息（温度等）
                const { x: mx, y: my } = mouseGridPosRef.current;
                let newHoverInfo: { name: string; temp: number } | null = null;
                
                if (mx >= 0 && mx < width && my >= 0 && my < height) {
                    const idx = my * width + mx;
                    const p = grid.pixels[idx];
                    if (p && !p.empty) {
                        emptyFramesRef.current = 0; // 重置空帧计数
                        newHoverInfo = {
                            name: p.name,
                            temp: Math.round(p.temperature)
                        };
                    } else {
                        emptyFramesRef.current++;
                    }
                } else {
                    emptyFramesRef.current++;
                }

                // 防抖逻辑：只有连续15帧（约0.25秒）检测不到粒子，才认为是真正的空
                // 这样可以避免粒子运动或鼠标微小移动导致的闪烁
                if (newHoverInfo === null && emptyFramesRef.current < 15) {
                    newHoverInfo = currentHoverInfoRef.current;
                }

                // 比较新旧值，避免不必要的重渲染
                const prev = currentHoverInfoRef.current;
                const shouldUpdate = 
                    (prev === null && newHoverInfo !== null) ||
                    (prev !== null && newHoverInfo === null) ||
                    (prev !== null && newHoverInfo !== null && (prev.name !== newHoverInfo.name || prev.temp !== newHoverInfo.temp));

                if (shouldUpdate) {
                    currentHoverInfoRef.current = newHoverInfo;
                    setHoverInfo(newHoverInfo);
                }

                updateBuffer(grid, buffer);
                
                // 上传纹理
                source.update();
                
                // 更新调试信息
                if (gravityEnabled || simulateGravity) {
                    // debugInfo 已由 hooks 或 sensors 更新
                } else {
                    // setDebugInfo(`P: ${grid.pixels.reduce((acc, p) => acc + (p ? 1 : 0), 0)}`);
                }
            });

        };

        init();

        return () => {
            // 清理
            appRef.current?.destroy(true, { children: true, texture: true });
            appRef.current = null;
            window.removeEventListener('wheel', (e) => e.preventDefault());
        };
    }, []);

    return (
        <div ref={containerRef} className="w-full h-[100dvh] bg-black overflow-hidden relative touch-none select-none">
            <div className="absolute top-4 right-4 z-20 pointer-events-auto">
                <button
                    className="bg-slate-900/70 backdrop-blur-md border border-slate-700/40 text-slate-100/90 hover:text-white px-3 py-1.5 rounded-full text-xs font-medium transition-colors"
                    onClick={() => {
                        const url = new URL(window.location.href);
                        const pathname = url.pathname;
                        const rest = pathname === "/en" ? "" : pathname.startsWith("/en/") ? pathname.slice(3) : pathname === "/zh" ? "" : pathname.startsWith("/zh/") ? pathname.slice(3) : pathname;
                        url.pathname = (locale === "en" ? "/zh" : "/en") + rest;
                        window.location.href = url.toString();
                    }}
                    type="button"
                    title={locale === "en" ? "切换到中文" : "Switch to English"}
                >
                    {locale === "en" ? "中文" : "EN"}
                </button>
            </div>
            {/* Top Left Status Bar: Gravity + Info */}
            <div className="absolute top-4 left-4 z-10 flex items-center gap-4 pointer-events-none">
                {gravityEnabled && (
                    <GravityCompass 
                        gravityVector={gravityVector} 
                    />
                )}
                
                <InfoPanel hoverInfo={hoverInfo} locale={locale} />
            </div>
            
            {/* Bottom Controls */}
            <div 
                className="absolute left-1/2 -translate-x-1/2 z-10 max-w-[95vw] flex flex-col items-center gap-3"
                style={{ bottom: 'max(1.5rem, env(safe-area-inset-bottom) + 0.5rem)' }}
            >
                
                {/* Hints Bar */}
                <HintsBar
                    hintsRef={hintsRef}
                    isBrushPanelOpen={isBrushPanelOpen}
                    setIsBrushPanelOpen={setIsBrushPanelOpen}
                    brushSize={brushSize}
                    setBrushSize={(size) => {
                        setBrushSize(size);
                        brushSizeRef.current = size;
                    }}
                    brushPanelRef={brushPanelRef}
                    gravityEnabled={gravityEnabled}
                    toggleGravity={toggleGravity}
                    simulateGravity={simulateGravity}
                    setSimulateGravity={setSimulateGravity}
                    setGravityEnabled={setGravityEnabled}
                    isLocalDev={isLocalDev}
                    locale={locale}
                />

                {/* Material Selection Panel */}
                <MaterialPanel
                    isOpen={isMaterialPanelOpen}
                    selectedParticle={selectedParticle}
                    onSelect={(type) => {
                        setSelectedParticle(type);
                        setIsMaterialPanelOpen(false);
                    }}
                    panelRef={materialPanelRef}
                    locale={locale}
                />

                {/* Tools Selection Panel */}
                <ToolsPanel
                    isOpen={isToolsPanelOpen}
                    selectedParticle={selectedParticle}
                    onSelect={(type) => {
                        setSelectedParticle(type);
                        setIsToolsPanelOpen(false);
                    }}
                    panelRef={toolsPanelRef}
                    locale={locale}
                />

                {/* Main Toolbar */}
                <MainToolbar
                    currentMaterial={currentMaterial}
                    selectedParticle={selectedParticle}
                    setSelectedParticle={setSelectedParticle}
                    isMaterialPanelOpen={isMaterialPanelOpen}
                    setIsMaterialPanelOpen={setIsMaterialPanelOpen}
                    currentTool={currentTool}
                    isToolsPanelOpen={isToolsPanelOpen}
                    setIsToolsPanelOpen={setIsToolsPanelOpen}
                    enableGlow={enableGlow}
                    setEnableGlow={setEnableGlow}
                    onClear={() => gridRef.current?.clear()}
                    toolbarRef={toolbarRef}
                    closePanels={closePanels}
                    locale={locale}
                />
            </div>
            
            {/* CSS for hiding scrollbar if needed, though usually handled by global css or plugin */}
            <style>{`
                .no-scrollbar::-webkit-scrollbar {
                    display: none;
                }
                .no-scrollbar {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>
        </div>
    );
};
